import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import {motion, useScroll, useSpring, useTransform} from 'framer-motion'
import Image from 'next/image'
import {useLinkResolver} from 'prismicio'
import {useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {CTAButtonWithArrow} from 'src/common/CTAButtonWithArrow'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import type {Context} from 'src/common/types'

/**
 * Props for `StresstimonialsHero`.
 */
export type StresstimonialsHeroProps = SliceComponentProps<Content.StresstimonialsHeroSlice>

/**
 * Component for "StresstimonialsHero" Slices.
 */
const StresstimonialsHero = ({
  slice,
  context: {pageUid, buttonsData},
  index: componentIndex,
}: StresstimonialsHeroProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const sanitizedHeading = sanitizeHtml(slice.primary.title ?? '')
  const dashedLineRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const [stickersVisible, setStickersVisible] = useState(false)
  const [shouldHideStickers, setShouldHideStickers] = useState(false)
  const [buttonBottomPosition, setButtonBottomPosition] = useState(-1000) // Start off-screen
  const initialBottomPositionRef = useRef<number | null>(null)
  const hasInitializedRef = useRef(false)
  const footerRef = useRef<HTMLElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const previousBottomRef = useRef<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.cta_button,
  })

  // Track viewport scroll progress for desktop animations
  const {scrollY} = useScroll()

  // Animate stickers out over first 1500px of scroll
  const maxScrollDistance = 1500

  // Transform values with spring physics for smooth movement
  const rawLeftX = useTransform(scrollY, [0, maxScrollDistance], [0, -70])
  const rawRightX = useTransform(scrollY, [0, maxScrollDistance], [0, 110])
  const rawTopY = useTransform(scrollY, [0, maxScrollDistance], [0, -190])

  const leftX = useSpring(rawLeftX, {stiffness: 150, damping: 25, mass: 0.5})
  const rightX = useSpring(rawRightX, {stiffness: 150, damping: 25, mass: 0.5})
  const topY = useSpring(rawTopY, {stiffness: 150, damping: 25, mass: 0.5})

  // Detect mobile and tablet viewports
  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth
      setIsMobile(width < 768)
      setIsTablet(width >= 768 && width < 1024)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport, {passive: true})

    return () => window.removeEventListener('resize', checkViewport)
  }, [])

  // Show stickers on mount
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStickersVisible(true)
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  // Mobile/tablet only: toggle stickers visibility on scroll
  useEffect(() => {
    if (!isMobile) {
      return
    }

    const handleScroll = () => {
      const scrolled = window.scrollY > 200
      setShouldHideStickers(scrolled)
    }

    window.addEventListener('scroll', handleScroll, {passive: true})
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMobile])

  // Cache footer element on mount
  useEffect(() => {
    footerRef.current = document.querySelector('footer')
  }, [])

  // Calculate and update button bottom position based on scroll
  useEffect(() => {
    const updateButtonPosition = () => {
      // Cancel any pending RAF
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        if (!headingRef.current) {
          return
        }

        const viewportHeight = window.innerHeight
        const currentScrollY = window.scrollY
        const buttonHeight = 40
        const gapBelowHeading = 64 // Gap between heading and button initially

        // Calculate where heading is in the viewport
        const headingRect = headingRef.current.getBoundingClientRect()
        const headingBottom = headingRect.bottom

        // Initialize the starting bottom position (where button appears below heading at scroll 0)
        if (!hasInitializedRef.current && headingRect.height > 0) {
          // Position button below heading with gap
          const initialBottom = viewportHeight - headingBottom - gapBelowHeading - buttonHeight
          initialBottomPositionRef.current = initialBottom
          setButtonBottomPosition(initialBottom)
          previousBottomRef.current = initialBottom
          hasInitializedRef.current = true
          return
        }

        if (initialBottomPositionRef.current === null) {
          return
        }

        const initialBottom = initialBottomPositionRef.current
        const targetBottom = 32 // Final sticky position
        const transitionDistance = 600 // Transition over 600px of scroll

        // Use cached footer element
        let footerInViewport = false

        if (footerRef.current) {
          const footerRect = footerRef.current.getBoundingClientRect()

          let gapAboveFooter = 250 // Desktop default

          if (isMobile) {
            gapAboveFooter = 220
          } else if (isTablet) {
            gapAboveFooter = 350
          }

          // Check if footer is in viewport
          footerInViewport = footerRect.top < viewportHeight + gapAboveFooter
        }

        // Calculate button position based on scroll
        let newBottom: number

        if (footerInViewport) {
          // Hide button when footer is in view
          newBottom = -1000
        } else if (currentScrollY < transitionDistance) {
          // Transitioning: interpolate from initial to target over 600px
          const progress = currentScrollY / transitionDistance
          newBottom = initialBottom + (targetBottom - initialBottom) * progress
        } else {
          // Sticky position
          newBottom = targetBottom
        }

        // Only update state if the value actually changed
        if (previousBottomRef.current !== newBottom) {
          setButtonBottomPosition(newBottom)
          previousBottomRef.current = newBottom
        }
      })
    }

    // Initial calculation with delay for layout stability
    const timer = setTimeout(updateButtonPosition, 100)

    const handleResize = () => {
      // Reinitialize cached position on any resize (width or height)
      hasInitializedRef.current = false
      initialBottomPositionRef.current = null
      updateButtonPosition()
    }

    window.addEventListener('scroll', updateButtonPosition, {passive: true})
    window.addEventListener('resize', handleResize, {passive: true})

    return () => {
      clearTimeout(timer)
      window.removeEventListener('scroll', updateButtonPosition)
      window.removeEventListener('resize', handleResize)

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [isMobile, isTablet])

  const stickyImages = [
    {
      field: slice.primary.left_sticky_image,
      className: 'pointer-events-none fixed left-0 top-[330px] sm:top-[140px] z-10 w-[90px] md:w-[185px]',
      direction: 'left',
    },
    {
      field: slice.primary.top_left_sticky_image,
      className: 'pointer-events-none fixed left-[155px] md:left-0 top-[-32px] z-10 w-[310px] md:w-[520px] md:top-0',
      direction: 'top',
    },
    {
      field: slice.primary.top_right_sticky_image,
      className: 'pointer-events-none fixed right-0 top-0 z-10 hidden w-[420px] md:block md:w-[500px]',
      direction: 'top',
    },
    {
      field: slice.primary.right_sticky_image,
      className: 'pointer-events-none fixed right-0 top-175 z-10 hidden w-[100px] md:block',
      direction: 'right',
    },
  ].filter((image) => isFilled.image(image.field))

  return (
    <Container
      backgroundColor="Transparent"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      extraPaddingTop
      className="!pb-[105px] !pt-[135px] md:!pb-5 md:!pt-[160px] lg:!pt-[112px]"
    >
      <style>{`
        /* CSS animations for mobile/tablet only */
        @media (max-width: 767px) {
          .sticker-base {
            transition: transform 4s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .sticker-left-hidden {
            transform: translateX(-70px);
          }

          .sticker-left-visible {
            transform: translateX(0);
          }

          .sticker-left-exit {
            transform: translateX(-70px);
          }

          .sticker-right-hidden {
            transform: translateX(110px);
          }

          .sticker-right-visible {
            transform: translateX(0);
          }

          .sticker-right-exit {
            transform: translateX(110px);
          }

          .sticker-top-hidden {
            transform: translateY(-190px);
          }

          .sticker-top-visible {
            transform: translateY(0);
          }

          .sticker-top-exit {
            transform: translateY(-190px);
          }
        }
      `}</style>
      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 md:px-10 md:pt-12 xl:px-0">
        <div className="w-full max-w-[800px]">
          <div className="mb-3 flex items-center justify-center gap-8">
            {isFilled.keyText(slice.primary.left_side_text) && (
              <div className="flex-1 border-y border-dashed border-moss-black py-2">
                <p className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.6px] text-moss-black">
                  {slice.primary.left_side_text}
                </p>
              </div>
            )}

            <div className="shrink-0">
              <Image
                src="/gif/Header_motion.gif"
                alt="Stresstimonials"
                width={141}
                height={70}
                className="h-[50px] w-auto md:h-[70px]"
                unoptimized
              />
            </div>

            {isFilled.keyText(slice.primary.right_side_text) && (
              <div className="flex-1 border-y border-dashed border-moss-black py-2 ">
                <p className="whitespace-nowrap text-end text-10 font-semibold uppercase tracking-[0.6px] text-moss-black">
                  {slice.primary.right_side_text}
                </p>
              </div>
            )}
          </div>
        </div>

        <h1
          ref={headingRef}
          className="max-w-[546px] text-center text-[80px] font-bold leading-[0.85] tracking-[-1.28px] text-[#131212] md:text-[96px] md:tracking-[-1.92px] lg:text-[128px] lg:tracking-[-2.56px]"
        >
          <span dangerouslySetInnerHTML={{__html: sanitizedHeading}} />
        </h1>

        <div ref={dashedLineRef} className="mt-8 w-full max-w-[800px] border-b border-dashed border-moss-black " />
      </div>

      {/* Fixed button - hidden when footer is in view */}
      {primaryButtonData && (
        <div
          className="fixed left-1/2 z-40 -translate-x-1/2 transition-opacity duration-300"
          style={{
            bottom: `${buttonBottomPosition}px`,
            opacity: buttonBottomPosition < -500 ? 0 : 1,
            pointerEvents: buttonBottomPosition < -500 ? 'none' : 'auto',
          }}
        >
          <CTAButtonWithArrow
            label={primaryButtonData.label || ''}
            href={linkResolver(primaryButtonData.link, 'stresstimonials-cta-button')}
            trackingData={{
              mktgId: primaryButtonData.marketing_id || '',
              location: `component-${componentIndex}-stresstimonials-hero`,
              dataType: primaryButtonData.intent || 'stresstimonials-cta-button',
              pageName: pageUid,
              label: primaryButtonData.label || '',
            }}
            id={`component-${componentIndex}-stresstimonials-hero-cta`}
          />
        </div>
      )}

      {stickyImages.map((image, index) => {
        const getStateClass = () => {
          if (shouldHideStickers) {
            return `sticker-${image.direction}-exit`
          }

          if (stickersVisible) {
            return `sticker-${image.direction}-visible`
          }

          return `sticker-${image.direction}-hidden`
        }

        // Get the appropriate motion value for this image direction
        const getMotionStyle = () => {
          if (isMobile) {
            return undefined // Let CSS classes handle mobile
          }

          // Desktop: use framer-motion values
          if (image.direction === 'left') {
            return {x: leftX}
          } else if (image.direction === 'right') {
            return {x: rightX}
          } else if (image.direction === 'top') {
            return {y: topY}
          }

          return undefined
        }

        const StickerWrapper = motion.div

        return (
          <StickerWrapper
            key={index}
            // eslint-disable-next-line tailwindcss/no-custom-classname -- Custom CSS classes defined in style tag
            className={cn(image.className, 'sticker-base', isMobile && getStateClass())}
            style={getMotionStyle()}
          >
            <PrismicNextImage field={image.field} className="size-full object-cover" />
          </StickerWrapper>
        )
      })}
    </Container>
  )
}

export default StresstimonialsHero
